import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { cn } from '#/lib/utils'
import { Badge } from '#/components/ui/badge'

const tones = {
  primary: 'bg-accent text-accent-foreground',
  green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  blue: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
}

export interface ChoiceCardProps {
  icon: ReactNode
  title: string
  description?: string
  badge?: string
  tone?: keyof typeof tones
  selected?: boolean
  onClick?: () => void
  to?: string
  href?: string
  search?: Record<string, string>
  footer?: ReactNode
  className?: string
  testId?: string
}

/** Selection card (landing, /plan, tech step, consulting) — spec/05 §4. */
export function ChoiceCard({ icon, title, description, badge, tone = 'primary', selected, onClick, to, href, search, footer, className, testId }: ChoiceCardProps) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl', tones[tone])}>{icon}</span>
        {badge ? (
          <Badge className="h-5 rounded-full px-2 text-[10px] font-semibold uppercase tracking-wide">{badge}</Badge>
        ) : null}
      </div>
      <div className="mt-3 space-y-1">
        <div className="font-heading text-base font-semibold leading-tight">{title}</div>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </>
  )
  const cls = cn(
    'group flex h-full w-full flex-col rounded-2xl border bg-card p-5 text-left transition-colors hover:border-primary/60',
    selected ? 'border-primary bg-primary/5' : 'border-border',
    className,
  )
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testId}>{inner}</a>
  if (to) return <Link to={to as '/'} search={search as never} className={cls} data-testid={testId}>{inner}</Link>
  return (
    <button type="button" onClick={onClick} className={cls} aria-pressed={selected} data-testid={testId}>
      {inner}
    </button>
  )
}
